import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenant from '../../config/tenant';
import { getAuthKeyFromLocalStorage } from './employeeSlice';
import attendanceService from '../../services/AttendanceService';
import holidayService from '../../services/holidayService';

async function attendanceFetch(url, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['auth-key'] = token;
  const res = await fetch(url, { method: 'GET', headers });
  if (res.status === 404) return { __noData: true };
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text || `HTTP error ${res.status}`);
  }
  return res.json();
}

// ─── Helper: build YYYY-MM-DD from any date input ─────────────────
const toYMD = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ─── Fetch Approved Leaves (per employee OR all) ──────────────────
export const fetchApprovedLeaves = createAsyncThunk(
  'attendance/fetchApprovedLeaves',
  async ({ employeeServiceNo } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthKeyFromLocalStorage();
      const url = employeeServiceNo
        ? `${tenant.api.baseUrl}/LeaveApprovels/GetAllRequestLeaveList?p_leaveStatus=APPROVED&p_empServiceNo=${encodeURIComponent(employeeServiceNo)}`
        : `${tenant.api.baseUrl}/LeaveApprovels/GetAllRequestLeaveList?p_leaveStatus=APPROVED`;
      const data = await attendanceFetch(url, token);
      if (data?.__noData) return [];
      const rs = data?.ResultSet ?? data?.Result ?? data?.Data;
      return Array.isArray(rs) ? rs : rs ? [rs] : [];
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load leaves');
    }
  }
);

// ─── Fetch Company Holidays ───────────────────────────────────────
export const fetchHolidays = createAsyncThunk(
  'attendance/fetchHolidays',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthKeyFromLocalStorage();
      const url = `${tenant.api.baseUrl}/Holiday/GetAllHolidays`;
      const data = await attendanceFetch(url, token);
      if (data?.__noData) return [];
      const rs = data?.ResultSet ?? data?.Result ?? data?.Data;
      return Array.isArray(rs) ? rs : rs ? [rs] : [];
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load holidays');
    }
  }
);


export async function getAttendanceCardRows({ serviceNo, month }) {
  const cleanServiceNo = String(serviceNo || '').trim();

  let yearStr, monthStr;
  if (month && String(month).includes('-')) {
    const parts = String(month).split('-');
    yearStr = parts[0];
    monthStr = parts[1].padStart(2, '0');
  } else {
    const now = new Date();
    yearStr = String(now.getFullYear());
    monthStr = String(month || now.getMonth() + 1).padStart(2, '0');
  }

  const attendanceRes = await attendanceService.GetFilteredAttendanceDetails({
    year: yearStr,
    month: monthStr,
    sno: cleanServiceNo,
  });

  if (!attendanceRes) return [];

  if (attendanceRes.StatusCode !== undefined && attendanceRes.StatusCode !== 200) {
    if (
      attendanceRes.StatusCode === 404 ||
      /no\s*(data|record)/i.test(attendanceRes.Message || '')
    ) {
      return [];
    }
    throw new Error(
      attendanceRes.Message || `Server error: status ${attendanceRes.StatusCode}`
    );
  }

  const rawRows = attendanceRes.ResultSet;
  return Array.isArray(rawRows) ? rawRows : rawRows ? [rawRows] : [];
}

// ─── Single employee thunk ────────────────────────────────────────
export const fetchAttendanceCard = createAsyncThunk(
  'attendance/fetchCard',
  async ({ serviceNo, month }, { rejectWithValue }) => {
    if (!serviceNo) return rejectWithValue('Service number is required.');
    if (!month) return rejectWithValue('Month is required.');
    try {
      const rows = await getAttendanceCardRows({ serviceNo, month });
      return { serviceNo, month, rows };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error.');
    }
  }
);

// ─── All employees thunk ──────────────────────────────────────────
export const fetchAllAttendanceCard = createAsyncThunk(
  'attendance/fetchAllCard',
  async ({ month }, { rejectWithValue }) => {
    if (!month) return rejectWithValue('Month is required.');
    try {
      const rows = await getAttendanceCardRows({ serviceNo: '', month });
      return { serviceNo: '__ALL__', month, rows };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error.');
    }
  }
);

const initialState = {
  serviceNo: null,
  month: null,
  rows: [],
  leaves: [],
  holidays: [],
  loading: false,
  leavesLoading: false,
  holidaysLoading: false,
  error: null,
  cache: {},
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearAttendanceError(state) {
      state.error = null;
    },
    resetAttendance(state) {
      state.serviceNo = null;
      state.month = null;
      state.rows = [];
      state.leaves = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Single employee ────────────────────────────────────────
      .addCase(fetchAttendanceCard.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.serviceNo = action.meta.arg.serviceNo;
        state.month = action.meta.arg.month;
        state.rows = [];
      })
      .addCase(fetchAttendanceCard.fulfilled, (state, action) => {
        const { serviceNo, month, rows } = action.payload; 
        state.loading = false;
        state.serviceNo = serviceNo;
        state.month = month;
        state.rows = rows;
        state.cache[`${serviceNo}_${month}`] = rows;
      })
      .addCase(fetchAttendanceCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load attendance';
        state.rows = [];
      })

      // ── All employees ──────────────────────────────────────────
      .addCase(fetchAllAttendanceCard.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.serviceNo = '__ALL__';
        state.month = action.meta.arg.month;
        state.rows = [];
      })
      .addCase(fetchAllAttendanceCard.fulfilled, (state, action) => {
        const { serviceNo, month, rows } = action.payload; 
        state.loading = false;
        state.serviceNo = serviceNo;
        state.month = month;
        state.rows = rows;
        state.cache[`ALL_${month}`] = rows;
      })
      .addCase(fetchAllAttendanceCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load attendance';
        state.rows = [];
      })

      // ── Leaves ─────────────────────────────────────────────────
      .addCase(fetchApprovedLeaves.pending, (state) => {
        state.leavesLoading = true;
      })
      .addCase(fetchApprovedLeaves.fulfilled, (state, action) => {
        state.leavesLoading = false;
        state.leaves = action.payload || [];
      })
      .addCase(fetchApprovedLeaves.rejected, (state) => {
        state.leavesLoading = false;
        state.leaves = [];
      })

      // ── Holidays ───────────────────────────────────────────────
      .addCase(fetchHolidays.pending, (state) => {
        state.holidaysLoading = true;
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.holidaysLoading = false;
        state.holidays = action.payload || [];
      })
      .addCase(fetchHolidays.rejected, (state) => {
        state.holidaysLoading = false;
        state.holidays = [];
      });
  },
});

export const { clearAttendanceError, resetAttendance } = attendanceSlice.actions;
export default attendanceSlice.reducer;